import api from './api';

export interface IoTDevice {
  id: number;
  device_id: string;
  device_name: string;
  device_type: string;
  mqtt_topic_prefix: string;
  location?: string;
  machine?: string;
  is_active: boolean;
  last_seen?: string;
  configuration?: any;
  created_by: number;
  created_by_username?: string;
  created_at: string;
  updated_at: string;
  total_batches?: number;
}

export interface DeviceStatus {
  id: number;
  device_id: string;
  state: string;
  wifi_rssi?: number;
  free_heap?: number;
  uptime?: number;
  message?: string;
  received_at: string;
}

export interface FabricWidthBatch {
  id: number;
  device_id: string;
  batch_number: number;
  status: 'running' | 'paused' | 'completed';
  total_length: number;
  defect_length: number;
  defect_count: number;
  total_run_time: number;
  defect_percentage: number;
  started_at: string;
  completed_at?: string;
  device_name?: string;
  location?: string;
}

export interface FabricWidthData {
  id: number;
  device_id: string;
  batch_number: number;
  current_width: number;
  target_width: number;
  total_length: number;
  defect_length: number;
  defect_count: number;
  in_defect: boolean;
  run_time: number;
  received_at: string;
}

export interface FabricWidthDefect {
  id: number;
  device_id: string;
  batch_number: number;
  defect_number: number;
  width: number;
  defect_length: number;
  severity: 'low' | 'medium' | 'high';
  detected_at: string;
}

export interface DashboardStats {
  activeDevices: number;
  runningBatches: number;
  defectsToday: number;
  lengthToday: number;
  recentBatches: FabricWidthBatch[];
}

// IoT Devices API
export const iotDevicesService = {
  // Get all devices
  getAll: (params?: { device_type?: string; is_active?: boolean }) => {
    return api.get<IoTDevice[]>('/iot-devices', { params });
  },

  // Get device types
  getDeviceTypes: () => {
    return api.get<{ value: string; label: string }[]>('/iot-devices/types');
  },

  // Get device by ID
  getById: (id: number) => {
    return api.get<IoTDevice>(`/iot-devices/${id}`);
  },

  // Create device
  create: (data: Partial<IoTDevice>) => {
    return api.post<{ message: string; id: number }>('/iot-devices', data);
  },

  // Update device
  update: (id: number, data: Partial<IoTDevice>) => {
    return api.put<{ message: string }>(`/iot-devices/${id}`, data);
  },

  // Delete device
  delete: (id: number) => {
    return api.delete<{ message: string }>(`/iot-devices/${id}`);
  },

  // Get device status
  getDeviceStatus: (id: number) => {
    return api.get<DeviceStatus[]>(`/iot-devices/${id}/status`);
  },
};

// Fabric Width API
export const fabricWidthService = {
  // Get dashboard stats
  getDashboardStats: () => {
    return api.get<DashboardStats>('/fabric-width/dashboard/stats');
  },

  // Get all batches
  getAllBatches: (params?: {
    device_id?: string;
    status?: string;
    start_date?: string;
    end_date?: string;
  }) => {
    return api.get<FabricWidthBatch[]>('/fabric-width/batches', { params });
  },

  // Get batch details
  getBatchDetails: (deviceId: string, batchNumber: number) => {
    return api.get<{
      batch: FabricWidthBatch;
      measurements: FabricWidthData[];
      defects: FabricWidthDefect[];
    }>(`/fabric-width/batches/${deviceId}/${batchNumber}`);
  },

  // Get real-time data
  getRealTimeData: (deviceId: string, batchNumber: number) => {
    return api.get<{
      latest: FabricWidthData | null;
      recentDefects: FabricWidthDefect[];
    }>(`/fabric-width/batches/${deviceId}/${batchNumber}/realtime`);
  },

  // Get measurement history
  getMeasurementHistory: (deviceId: string, batchNumber: number, limit?: number) => {
    return api.get<Array<{
      timestamp: string;
      current_width: number;
      defect_length: number;
      total_length: number;
      in_defect: boolean;
    }>>(`/fabric-width/batches/${deviceId}/${batchNumber}/history`, {
      params: { limit },
    });
  },

  // Get defect analysis
  getDefectAnalysis: (deviceId: string, batchNumber: number) => {
    return api.get<{
      total_defects: number;
      total_defect_length: number;
      avg_defect_width: number;
      min_width: number;
      max_width: number;
      high_severity: number;
      medium_severity: number;
      low_severity: number;
    }>(`/fabric-width/batches/${deviceId}/${batchNumber}/defects/analysis`);
  },

  // Export batch data
  exportBatchData: (deviceId: string, batchNumber: number) => {
    return api.get(`/fabric-width/batches/${deviceId}/${batchNumber}/export`, {
      responseType: 'blob',
    });
  },

  // Delete batch
  deleteBatch: (deviceId: string, batchNumber: string) => {
    return api.delete<{ message: string }>(`/fabric-width/batches/${deviceId}/${batchNumber}`);
  },
};
